import React, { useMemo, useRef, useState } from "react";
import { AlertTriangle, Camera, Check, ImageOff, Loader2, Trash2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, API } from "../lib/api";
import CameraCapture from "./CameraCapture";
import PhotoGallery from "./PhotoGallery";
import { useRole } from "../context/RoleContext";
import { toast } from "sonner";
import FieldShopLinks from "../../drawings/FieldShopLinks";

const photoUrl = (p) => {
  const token = localStorage.getItem("auth_token");
  return `${API}/completion-photos/${p.storage_path}${token ? `?auth=${token}` : ""}`;
};

export const completionKey = (it, scope) =>
  `${it.building || ""}||${it.floor || ""}||${it.area || ""}||${it.room_number || ""}||${it.room_type || ""}||${scope || ""}`;

// Units still to install per FRS item. Older records only flagged whole items,
// so those count as "all of it left".
const initialRemaining = (completion, items) => {
  const byItem = completion?.remaining_units_by_item || null;
  const out = {};
  for (const i of items) {
    const qty = Number(i.quantity || 0);
    if (byItem && byItem[i.id] !== undefined) {
      const left = Math.min(qty, Number(byItem[i.id] || 0));
      if (left > 0) out[i.id] = left;
    } else if ((completion?.remaining_item_ids || []).includes(i.id)) {
      out[i.id] = qty;
    }
  }
  return out;
};

/**
 * Field completion card for one room × scope.
 * readOnly = the Project FRS grid view (history only, no editing).
 */
export default function CompletionCard({
  open, onOpenChange, projectId, scope, room, items, completion, author, readOnly = false, onSaved,
  onCreateIssue, issues = [], roomColumns = [], presetRemaining = null, origin = "", shopLinks = null,
}) {
  const startRemaining = () => (presetRemaining ? { ...presetRemaining } : initialRemaining(completion, items));
  const [mode, setMode] = useState(completion?.mode === "full" ? "full" : "partial");
  const [remaining, setRemaining] = useState(startRemaining);
  const [countFor, setCountFor] = useState(null);   // item id whose "how many left" strip is open
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [camera, setCamera] = useState(false);          // multi-shot camera session
  const [gallery, setGallery] = useState(false);        // saved photos, full size
  const { role } = useRole();
  const fileRef = useRef(null);
  const startedRef = useRef(false);
  // On a phone the tap that closes the camera lands on this card's overlay a moment later (the
  // "ghost tap"), which used to dismiss the whole completion window and throw the photos away.
  // While a camera / gallery session is open — and for a beat after it closes — this card refuses
  // to be dismissed from the outside, so Done attaches the shots and the crew can still Save.
  const closeGuard = useRef(0);
  const holdOpen = () => camera || gallery || Date.now() < closeGuard.current;
  const requestClose = (o) => { if (o || !holdOpen()) onOpenChange(o); };

  // Reset the form each time the card opens for a different cell.
  const cellId = `${room?.key || ""}|${scope}`;
  const seenRef = useRef(cellId);
  if (open && (seenRef.current !== cellId || !startedRef.current)) {
    seenRef.current = cellId;
    startedRef.current = true;
    setMode(completion?.mode === "full" ? "full" : "partial");
    setRemaining(startRemaining());
    setCountFor(null);
    setNote("");
    setPhotos([]);
  }
  if (!open && startedRef.current) startedRef.current = false;

  const totalUnits = useMemo(() => items.reduce((s, i) => s + Number(i.quantity || 0), 0), [items]);
  const remainingUnits = useMemo(
    () => items.reduce((s, i) => s + Math.min(Number(i.quantity || 0), remaining[i.id] || 0), 0),
    [items, remaining],
  );
  // The FRS can hold the same product on more than one row for a room (two
  // sheet lines of 1 instead of one line of 2). The grid sums them, so the card
  // must too — one line per product, quantities added up.
  const groups = useMemo(() => {
    const by = new Map();
    for (const i of items) {
      const key = i.product_uid || i.product_code || i.id;
      if (!by.has(key)) {
        by.set(key, { key, label: i.product_description || "Item",
                      code: i.product_code || "", qty: 0, rows: [] });
      }
      const g = by.get(key);
      g.qty += Number(i.quantity || 0);
      g.rows.push(i);
    }
    return [...by.values()];
  }, [items]);

  const groupLeft = (g) => g.rows.reduce((s, r) => s + Math.min(Number(r.quantity || 0), remaining[r.id] || 0), 0);

  // Spread a group's remaining units over its rows so the saved per-row counts
  // still add up to what the installer chose.
  const setGroupLeft = (g, units) => setRemaining((cur) => {
    const n = { ...cur };
    let left = Math.max(0, Math.min(units, g.qty));
    for (const r of g.rows) {
      const take = Math.min(left, Number(r.quantity || 0));
      if (take > 0) n[r.id] = take; else delete n[r.id];
      left -= take;
    }
    return n;
  });
  const livePct = mode === "full"
    ? 100
    : totalUnits <= 0 ? 0 : Math.round(((totalUnits - remainingUnits) / totalUnits) * 100);
  const savedPhotos = completion?.photos || [];
  // Photo evidence at 100% is optional for now (requirement lifted by request).
  const canSave = !readOnly;

  const attach = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    await upload(files);
  };

  const upload = async (list) => {
    const files = [...list].filter((f) => f.type.startsWith("image/"));
    if (files.length !== list.length) toast.error("Only image files can be attached");
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is over 10MB`); continue; }
      const fd = new FormData();
      fd.append("project_id", projectId);
      fd.append("uploader", author || "");
      fd.append("file", f);
      try {
        const { data } = await api.post("/completions/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((cur) => [...cur, data]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Could not upload ${f.name}`);
      }
    }
    setUploading(false);
  };

  /** One shot from the live camera. The camera stays open — it calls this per frame. */
  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("project_id", projectId);
    fd.append("uploader", author || "");
    fd.append("file", file);
    const { data } = await api.post("/completions/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPhotos((cur) => [...cur, data]);
    return data;
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/completions", {
        project_id: projectId,
        scope_type: scope,
        building: room.building || "",
        floor: room.floor || "",
        area: room.area || "",
        room_number: room.room_number || "",
        room_type: room.room_type || "",
        mode,
        remaining_item_ids: Object.keys(remaining).filter((k) => remaining[k] > 0),
        remaining_units_by_item: remaining,
        note: origin && note ? `${note} (from the drawing ${origin})` : origin ? `Recorded from the drawing ${origin}` : note,
        photos,
        author: author || "",
      });
      toast.success(mode === "full" ? "Room marked 100% complete" : `Saved — ${livePct}% complete`);
      onSaved && onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save completion");
    }
    setBusy(false);
  };

  const roomLabel = [room?.room_number, room?.room_name].filter(Boolean).join(" · ") || "Room";
  // FRS plan references for this room — only the ones the FRS actually filled in.
  const planRefs = [
    ["Area", room?.area], ["Floor Plan", room?.floor_plan],
    ["Enlarged Floor Plan", room?.plan_sheet], ["Elevations", room?.elevation_sheet],
    ...roomColumns.map((c) => [c.label, room?.[`custom:${c.key}`]]),
  ].filter(([, v]) => String(v ?? "").trim() !== "");

  // Cluster 12 — only the person who recorded the progress or an admin may wipe it.
  const isAdmin = String(role || "").toLowerCase().match(/^(admin|management)$/) !== null;
  const samePerson = (a, b) => {
    const n = (s) => String(s || "").trim().toLowerCase();
    if (!n(a) || !n(b)) return false;
    const local = (s) => n(s).split("@")[0].replace(/\./g, " ");
    return n(a) === n(b) || local(a) === local(b) || local(a) === n(b) || n(a) === local(b);
  };
  const canDelete = !!completion && (isAdmin || samePerson(author, completion.author));

  const deleteProgress = async () => {
    setBusy(true);
    try {
      await api.delete(`/completions/${completion.id}`, { params: { actor: author || "", role: role || "" } });
      toast.success("Progress deleted");
      setConfirmDelete(false);
      onSaved && onSaved(null);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete progress");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-md p-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="completion-card"
                     onInteractOutside={(e) => { if (holdOpen()) e.preventDefault(); }}
                     onEscapeKeyDown={(e) => { if (holdOpen()) e.preventDefault(); }}>
        <DialogHeader className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-blue-50 dark:bg-gray-800">
          <DialogTitle className="text-base font-bold text-gray-900 dark:text-white">
            {readOnly ? "Completion" : "Complete"} · {roomLabel}
          </DialogTitle>
          <div className="text-[11px] text-gray-600 dark:text-gray-300 flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold text-purple-700 dark:text-purple-300">{scope}</span>
            <span>{totalUnits} units</span>
            {completion && (
              <span className="font-semibold text-emerald-700 dark:text-emerald-300" data-testid="completion-card-saved-pct">
                {Math.round((completion.percent_complete || 0) * 100)}% complete
              </span>
            )}
          </div>
          {shopLinks && (
            <div className="mt-1" data-testid="completion-card-shop-links"><FieldShopLinks links={shopLinks} roomNumber={room?.room_number} projectId={projectId} from="install" /></div>
          )}
          {!readOnly && planRefs.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid="completion-card-room-refs">
              {planRefs.map(([label, val]) => (
                <span
                  key={label}
                  data-testid={`completion-card-ref-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-[10px] leading-tight px-1.5 py-0.5 rounded border border-blue-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-100"
                >
                  <span className="uppercase tracking-tight text-gray-500 dark:text-gray-400">{label}</span>
                  <span className="ml-1 font-semibold">{val}</span>
                </span>
              ))}
            </div>
          )}
        </DialogHeader>

        <div className="px-4 py-3 overflow-y-auto space-y-3">
          {!readOnly && (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode("full")}
                data-testid="completion-mode-full"
                className={`h-12 rounded-lg border text-sm font-semibold ${mode === "full"
                  ? "bg-emerald-600 text-white border-emerald-700"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100 border-gray-300 dark:border-gray-600"}`}
              >
                100% Complete
              </button>
              <button
                type="button"
                onClick={() => setMode("partial")}
                data-testid="completion-mode-partial"
                className={`h-12 rounded-lg border text-sm font-semibold ${mode === "partial"
                  ? "bg-amber-500 text-white border-amber-600"
                  : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100 border-gray-300 dark:border-gray-600"}`}
              >
                Partial
              </button>
            </div>
          )}

          {!readOnly && mode === "partial" && (
            <div data-testid="completion-checklist">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-300 mb-1">
                Check what is still remaining
              </div>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-800">
                {groups.map((g) => {
                  const left = groupLeft(g);
                  const checked = left > 0;
                  const open2 = countFor === g.key && g.qty > 1;
                  return (
                    <div key={g.key} data-testid={`completion-item-${g.key}`}>
                      <label className="flex items-center gap-3 px-3 py-3 cursor-pointer active:bg-gray-50 dark:active:bg-gray-800">
                        <input
                          type="checkbox"
                          className="w-5 h-5"
                          checked={checked}
                          data-testid={`completion-item-check-${g.key}`}
                          onChange={() => {
                            if (checked) { setGroupLeft(g, 0); setCountFor(null); }
                            else { setGroupLeft(g, g.qty); setCountFor(g.qty > 1 ? g.key : null); }
                          }}
                        />
                        <span className="flex-1 min-w-0 text-sm">
                          <span className="font-mono text-[11px] text-gray-500 dark:text-gray-400 mr-1">{g.code || "—"}</span>
                          <span className="text-gray-900 dark:text-white">{g.label}</span>
                        </span>
                        {checked && g.qty > 1 ? (
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); setCountFor(open2 ? null : g.key); }}
                            data-testid={`completion-item-left-btn-${g.key}`}
                            className="tabular-nums text-sm font-semibold text-amber-700 dark:text-amber-200 border border-amber-300 dark:border-amber-500/60 bg-amber-50 dark:bg-amber-500/15 rounded-md px-2 py-0.5"
                          >
                            {left === g.qty ? `all ${g.qty}` : `${left} of ${g.qty}`} left
                          </button>
                        ) : (
                          <span className="tabular-nums text-sm text-gray-600 dark:text-gray-300">{g.qty}</span>
                        )}
                      </label>

                      {open2 && (
                        <div className="px-3 pb-3" data-testid={`completion-item-left-picker-${g.key}`}>
                          <div className="text-[11px] text-gray-600 dark:text-gray-300 mb-1.5">How many are left to install?</div>
                          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                            {Array.from({ length: g.qty }, (_, k) => g.qty - k).map((n) => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => { setGroupLeft(g, n); setCountFor(null); }}
                                data-testid={`completion-item-left-${g.key}-${n}`}
                                className={`h-10 min-w-[52px] shrink-0 rounded-lg border text-sm font-semibold tabular-nums ${
                                  n === left
                                    ? "bg-amber-500 text-white border-amber-600"
                                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100 border-gray-300 dark:border-gray-600"}`}
                              >
                                {n === g.qty ? `All ${g.qty}` : n}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 text-sm text-gray-700 dark:text-gray-200" data-testid="completion-live-pct">
                {totalUnits - remainingUnits} of {totalUnits} units installed ·{" "}
                <span className="font-bold text-emerald-700 dark:text-emerald-300">{livePct}%</span>
              </div>
            </div>
          )}

          {!readOnly && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-300 mb-1">
                Photos
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={attach}
                className="hidden"
                data-testid="completion-photo-input"
              />
              {/* Drop zone on the desktop, big tap target on a phone. */}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  upload(e.dataTransfer?.files || []);
                }}
                onPaste={(e) => {
                  const f = [...(e.clipboardData?.files || [])];
                  if (f.length) { e.preventDefault(); upload(f); }
                }}
                data-testid="completion-photo-dropzone"
                className={`rounded-lg border-2 border-dashed transition-colors ${
                  dragging ? "border-blue-500 bg-blue-50 dark:bg-blue-500/10" : "border-gray-300 dark:border-gray-600 bg-gray-50/60 dark:bg-gray-800/60"}`}
              >
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setCamera(true)}
                  className="w-full h-12 dark:text-gray-100"
                  data-testid="completion-add-photo-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                  {uploading ? "Uploading…" : "Take photos — the camera stays open"}
                </Button>
                <div className="pb-2 text-center text-[11px] text-gray-500 dark:text-gray-400">
                  {dragging ? "Drop the photos here" : "or drag photos here · paste from the clipboard"}
                </div>
              </div>
              {photos.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap" data-testid="completion-new-photos">
                  {photos.map((p) => (
                    <div key={p.id} className="relative">
                      <img src={photoUrl(p)} alt={p.filename} className="w-16 h-16 object-cover rounded border border-gray-200" />
                      <button
                        type="button"
                        onClick={() => setPhotos((cur) => cur.filter((x) => x.id !== p.id))}
                        className="absolute -top-1.5 -right-1.5 bg-white border border-gray-300 rounded-full p-0.5"
                        data-testid={`completion-remove-photo-${p.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {!readOnly && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-300 mb-1">Note</div>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="What's left, blockers, anything the next crew needs…"
                className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                data-testid="completion-note-input"
              />
            </div>
          )}

          {issues.filter((i) => i.status !== "resolved").length > 0 && (
            <div className="text-[12px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/40 rounded-lg px-3 py-2"
                 data-testid="completion-open-issues">
              {issues.filter((i) => i.status !== "resolved").length} open issue(s) on this room / scope
            </div>
          )}
          {readOnly && !completion && (
            <div className="text-sm text-gray-500 dark:text-gray-300 py-6 text-center" data-testid="completion-empty-state">
              No completion recorded yet for this room and scope.
              <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">Installers record progress from the Install tab.</div>
            </div>
          )}
          {canDelete && (
            <div className="border-t border-gray-200 pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmDelete(true)}
                className="w-full h-11 border-red-300 dark:border-red-500/60 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10"
                data-testid="completion-delete-progress-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete progress
              </Button>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                Recorded by {completion.author || "—"} · {isAdmin ? "you are an administrator" : "you recorded this"}
              </div>
            </div>
          )}
          {(savedPhotos.length > 0 || (completion?.notes || []).length > 0) && (
            <div className="border-t border-gray-200 pt-3" data-testid="completion-history">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-300 mb-1">History</div>
              {savedPhotos.length > 0 ? (
                <div className="flex gap-2 flex-wrap mb-2">
                  {savedPhotos.map((p) => (
                    <a key={p.id} href={photoUrl(p)} onClick={(e) => { e.preventDefault(); setGallery(true); }}
                       title={`${p.uploader || "unknown"} · ${new Date(p.uploaded_at).toLocaleString()}`}
                       data-testid={`completion-photo-${p.id}`}>
                      <img src={photoUrl(p)} alt={p.filename}
                           className="w-20 h-20 object-cover rounded border border-gray-200" />
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 w-20 truncate">
                        {new Date(p.uploaded_at).toLocaleDateString()} {new Date(p.uploaded_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 w-20 truncate">{p.uploader || "—"}</div>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1 mb-2">
                  <ImageOff className="w-3 h-3" /> no photos yet
                </div>
              )}
              {(completion?.notes || []).slice().reverse().map((n, idx) => (
                <div key={idx} className="text-[12px] text-gray-800 dark:text-gray-100 border-l-2 border-gray-200 dark:border-gray-700 pl-2 mb-1.5"
                     data-testid={`completion-note-${idx}`}>
                  {n.text}
                  <div className="text-[10px] text-gray-500 dark:text-gray-400">
                    {n.author || "—"} · {new Date(n.at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {!readOnly && (
          <DialogFooter className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex-wrap gap-2">
            {onCreateIssue && (
              <Button
                variant="outline"
                onClick={() => onCreateIssue()}
                className="h-11 mr-auto border-red-300 dark:border-red-500/60 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10"
                data-testid="completion-create-issue-btn"
              >
                <AlertTriangle className="w-4 h-4 mr-2" /> Create issue
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-gray-800" data-testid="completion-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={!canSave || busy}
              className="h-11 bg-blue-600 hover:bg-blue-700 text-white"
              title={!canSave ? "Read only" : ""}
              data-testid="completion-save-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save {mode === "full" ? "100%" : `${livePct}%`}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-sm" data-testid="completion-delete-confirm">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-red-700 dark:text-red-300">Are you sure?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-gray-700 dark:text-gray-200 space-y-1">
            <div>This permanently removes the recorded progress for:</div>
            <ul className="text-[13px] list-disc pl-5">
              <li data-testid="completion-delete-room">Room {roomLabel}</li>
              <li data-testid="completion-delete-scope">Scope {scope}</li>
              <li data-testid="completion-delete-pct">{Math.round((completion?.percent_complete || 0) * 100)}% complete</li>
              <li data-testid="completion-delete-photos">{savedPhotos.length} photo{savedPhotos.length === 1 ? "" : "s"}</li>
            </ul>
            <div className="text-[12px] text-gray-500 dark:text-gray-400">
              The checklist state, X markers and notes go too. Rolled-up percentages and remaining hours recalculate. The deletion is logged.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDelete(false)} className="h-10 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
                    data-testid="completion-delete-cancel">Cancel</Button>
            <Button onClick={deleteProgress} disabled={busy}
                    className="h-10 bg-red-600 hover:bg-red-700 text-white"
                    data-testid="completion-delete-confirm-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {camera && (
        <CameraCapture open upload={uploadOne} title={`Photos · ${roomLabel}`}
                       onDone={(recs) => { if (recs.length) toast.success(`${recs.length} photo${recs.length === 1 ? "" : "s"} attached to ${roomLabel}`, { description: "Save to record them on this room" }); }}
                       onDrop={(rec) => setPhotos((cur) => cur.filter((p) => p.id !== rec.id))}
                       onClose={() => { closeGuard.current = Date.now() + 800; setCamera(false); }} />
      )}
      {gallery && (
        <PhotoGallery open testid="completion-gallery" onClose={() => { closeGuard.current = Date.now() + 800; setGallery(false); }}
                      title={`Installation photos · ${roomLabel}`}
                      subtitle={`${scope} · ${completion?.mode === "full" ? "marked 100%" : `partial · ${Math.round((completion?.percent_complete || 0) * 100)}%`} · recorded by ${completion?.author || "—"}`}
                      photos={[...savedPhotos].sort((a, b) => String(b.uploaded_at).localeCompare(String(a.uploaded_at)))}
                      metaOf={() => `${roomLabel} · ${scope} · ${completion?.mode === "full" ? "100%" : "partial"}`}
                      emptyText="No installation photos on this record" />
      )}
    </Dialog>
  );
}
